import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';
import { RoleService } from 'src/app/services/role.service';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-image-upload',
  templateUrl: './image-upload.component.html',
  styleUrls: ['./image-upload.component.scss']
})
export class ImageUploadComponent implements OnInit {
  selectedFile: File | null = null;
  uploadStatus: string = '';
  isUploading: boolean = false;
  uploadedImageUrl: string | null = null;
  allImages: string[] = [];
  filteredImages: string[] = [];
  searchTerm: string = '';
  isLoadingImages: boolean = false;
  filePreviewUrl: string | null = null;
  isFileValid: boolean = false;
  validationMessage: string = '';
  viewingImage: string | null = null;
  toastMessage: string = '';
  toastType: string = '';
  uploadTimestamp: Date | null = null;
  uploadedImageSize: string = '';
  uploadedImageDimensions: string = '';

  // Gallery enhancement properties
  selectedImages: string[] = [];
  imageFilter: string = 'all';
  sortOption: string = 'name-asc';
  galleryView: string = 'grid';
  currentPage: number = 1;
  imagesPerPage: number = 12;
  paginatedImages: string[] = [];
  totalPages: number = 1;
  loadedImages: Set<string> = new Set();

  // File validation constants
  private maxFileSize = 20 * 1024 * 1024; // 20MB in bytes
  private allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/svg+xml', 'image/bmp'];

  constructor(
    private http: HttpClient, 
    private router: Router,
    private roleService: RoleService,
    private toastr: ToastrService
  ) { }

  ngOnInit(): void {
    // Check if user is authorized to access this page
    if (!this.roleService.isSuperAdmin()) {
      this.showToast('আপনার এই পেজে প্রবেশের অনুমতি নেই। সুপার অ্যাডমিন অধিকার প্রয়োজন।', 'error');
      this.router.navigate(['/admin/dashboard']);
      return;
    }
    
    this.loadAllImages();
  }

  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (!file) {
      this.clearSelectedFile();
      return;
    }

    this.selectedFile = file;
    this.validateFile();
    this.generatePreview();
    
    // Reset upload status
    this.uploadStatus = '';
    this.uploadedImageUrl = null;
  }

  private validateFile(): void {
    if (!this.selectedFile) {
      this.isFileValid = false;
      this.validationMessage = 'কোন ফাইল নির্বাচিত নয়';
      return;
    }

    // Check file type
    if (!this.allowedTypes.includes(this.selectedFile.type)) {
      this.isFileValid = false;
      this.validationMessage = 'অসমর্থিত ফাইল ফরম্যাট';
      return;
    }

    // Check file size
    if (this.selectedFile.size > this.maxFileSize) {
      this.isFileValid = false;
      this.validationMessage = 'ফাইল সাইজ ২০ মেগাবাইটের বেশি';
      return;
    }

    this.isFileValid = true;
    this.validationMessage = 'ফাইল বৈধ';
  }

  private generatePreview(): void {
    if (!this.selectedFile || !this.isFileValid) {
      this.filePreviewUrl = null;
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      this.filePreviewUrl = e.target?.result as string;
    };
    reader.readAsDataURL(this.selectedFile);
  }

  clearSelectedFile(): void {
    this.selectedFile = null;
    this.filePreviewUrl = null;
    this.isFileValid = false;
    this.validationMessage = '';
    this.uploadStatus = '';
    
    // Clear file input
    const fileInput = document.getElementById('imageFile') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  // File validation helper methods
  getFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  getValidationClass(): string {
    if (!this.selectedFile) return '';
    return this.isFileValid ? 'valid' : 'invalid';
  }

  getValidationIcon(): string {
    if (!this.selectedFile) return '';
    return this.isFileValid ? 'fa-check-circle' : 'fa-times-circle';
  }

  getValidationMessage(): string {
    return this.validationMessage;
  }

  // Upload status helper methods
  getStatusClass(): string {
    if (this.uploadStatus.includes('successfully') || this.uploadStatus.includes('সফলভাবে')) {
      return 'success';
    }
    if (this.uploadStatus.includes('failed') || this.uploadStatus.includes('ব্যর্থ')) {
      return 'error';
    }
    if (this.uploadStatus.includes('Uploading') || this.uploadStatus.includes('আপলোড হচ্ছে')) {
      return 'loading';
    }
    return 'info';
  }

  getStatusIcon(): string {
    const statusClass = this.getStatusClass();
    switch (statusClass) {
      case 'success': return 'fa-check-circle';
      case 'error': return 'fa-times-circle';
      case 'loading': return 'fa-spinner fa-spin';
      default: return 'fa-info-circle';
    }
  }

  uploadImage(): void {
    if (!this.selectedFile || !this.isFileValid) {
      this.uploadStatus = 'অনুগ্রহ করে একটি বৈধ ইমেজ ফাইল নির্বাচন করুন';
      this.showToast('অনুগ্রহ করে একটি বৈধ ইমেজ ফাইল নির্বাচন করুন', 'warning');
      return;
    }

    this.isUploading = true;
    this.uploadStatus = 'আপলোড হচ্ছে...';

    const formData = new FormData();
    formData.append('image', this.selectedFile);

    this.http.post(`${environment.apiBaseUrl}/api/admin/upload-image`, formData)
      .subscribe({
        next: (response: any) => {
          this.isUploading = false;
          this.uploadStatus = 'ইমেজ সফলভাবে আপলোড হয়েছে!';
          
          // Process the uploaded image URL the same way as gallery images
          let imageUrl = response.imageUrl;
          if (!imageUrl.startsWith(environment.apiBaseUrl)) {
            imageUrl = `${environment.apiBaseUrl}${imageUrl}`;
          }
          this.uploadedImageUrl = imageUrl;
          
          this.uploadTimestamp = new Date();
          this.uploadedImageSize = this.selectedFile ? this.getFileSize(this.selectedFile.size) : '';
          this.showToast('ইমেজ সফলভাবে আপলোড হয়েছে!', 'success');
          this.loadAllImages();
          this.clearSelectedFile();
        },
        error: (error) => {
          this.isUploading = false;
          this.uploadStatus = `আপলোড ব্যর্থ: ${error.message}`;
          this.showToast(`আপলোড ব্যর্থ: ${error.message}`, 'error');
          console.error('Upload error:', error);
        }
      });
  }

  loadAllImages(): void {
    this.isLoadingImages = true;
    this.http.get<string[]>(`${environment.apiBaseUrl}/api/admin/all-images`)
      .subscribe({
        next: (images) => {
          // Ensure all image URLs have the full API base URL
          this.allImages = images.map(image => {
            // Only add base URL if it's not already included
            if (!image.startsWith(environment.apiBaseUrl)) {
              return `${environment.apiBaseUrl}${image}`;
            }
            return image;
          });
          this.filteredImages = [...this.allImages]; // Initialize filtered images with all images
          this.cleanupSelections(); // Clean up selections after loading new images
          this.isLoadingImages = false;
          this.updatePagination();
        },
        error: (error) => {
          this.isLoadingImages = false;
          this.showToast('ইমেজ লোড করতে ব্যর্থ', 'error');
          console.error('Error loading images:', error);
        }
      });
  }

  filterImages(): void {
    let filtered = [...this.allImages];

    // Apply search filter
    if (this.searchTerm.trim()) {
      const term = this.searchTerm.toLowerCase().trim();
      filtered = filtered.filter(image => {
        const filename = image.split('/').pop() || '';
        return filename.toLowerCase().includes(term);
      });
    }

    // Apply type filter
    if (this.imageFilter !== 'all') {
      filtered = filtered.filter(image => {
        const extension = this.getImageFormat(image).toLowerCase();
        if (this.imageFilter === 'recent') {
          // For recent, we'd need timestamps - for now just return last 20
          return this.allImages.indexOf(image) >= this.allImages.length - 20;
        }
        return extension === this.imageFilter;
      });
    }

    this.filteredImages = filtered;
    this.currentPage = 1;
    this.cleanupSelections(); // Clean up selections after filtering
    this.updatePagination();
  }

  // Image helper methods
  getImageName(imageUrl: string): string {
    return imageUrl.split('/').pop() || 'Unknown';
  }

  getImageDimensions(imageUrl: string): string {
    // This would typically be determined when the image loads
    // For now, return a placeholder
    return 'Original Size';
  }

  trackByImageUrl(index: number, imageUrl: string): string {
    return imageUrl;
  }

  onImageLoad(event: any): void {
    // Mark image as loaded
    const imgSrc = event.target.src;
    this.loadedImages.add(imgSrc);
    // Could be used to get actual image dimensions
    // const img = event.target;
    // console.log(`Image dimensions: ${img.naturalWidth}x${img.naturalHeight}`);
  }

  // Image viewer methods
  viewImage(imageUrl: string): void {
    this.viewingImage = imageUrl;
  }

  closeImageViewer(): void {
    this.viewingImage = null;
  }

  copyImageUrl(imageUrl: string): void {
    navigator.clipboard.writeText(imageUrl).then(() => {
      this.showToast('ইমেজ URL কপি করা হয়েছে', 'success');
    }, (err) => {
      this.showToast('URL কপি করতে ব্যর্থ', 'error');
      console.error('Could not copy text: ', err);
    });
  }

  deleteImage(imageUrl: string): void {
    if (!confirm('আপনি কি নিশ্চিত যে এই ইমেজটি ডিলিট করতে চান? এই কাজটি পূর্বাবস্থায় ফেরানো যাবে না।')) {
      return;
    }

    // Extract the filename from the URL
    const filename = imageUrl.split('/').pop();
    if (!filename) {
      this.showToast('ফাইলের নাম নির্ধারণ করতে ব্যর্থ', 'error');
      return;
    }

    this.http.delete(`${environment.apiBaseUrl}/api/admin/delete-image/${filename}`)
      .subscribe({
        next: (response: any) => {
          this.showToast('ইমেজ সফলভাবে ডিলিট হয়েছে', 'success');
          // If this was the currently displayed uploaded image, clear it
          if (this.uploadedImageUrl === imageUrl) {
            this.uploadedImageUrl = null;
            this.uploadStatus = '';
          }
          // Close viewer if viewing deleted image
          if (this.viewingImage === imageUrl) {
            this.closeImageViewer();
          }
          // Refresh the image list
          this.loadAllImages();
        },
        error: (error) => {
          this.showToast(`ইমেজ ডিলিট করতে ব্যর্থ: ${error.message}`, 'error');
          console.error('Delete error:', error);
        }
      });
  }

  // Toast notification methods
  private showToast(message: string, type: string = 'info'): void {
    this.toastMessage = message;
    this.toastType = type;
    setTimeout(() => this.closeToast(), 5000);
  }

  closeToast(): void {
    this.toastMessage = '';
    this.toastType = '';
  }

  getToastIcon(): string {
    switch (this.toastType) {
      case 'success': return 'fa-check-circle';
      case 'error': return 'fa-times-circle';
      case 'warning': return 'fa-exclamation-triangle';
      default: return 'fa-info-circle';
    }
  }

  // New methods for enhanced upload result section
  onUploadedImageLoad(event: any): void {
    const img = event.target;
    this.uploadedImageDimensions = `${img.naturalWidth} x ${img.naturalHeight}`;
  }

  getUploadedImageName(): string {
    if (!this.uploadedImageUrl) return '';
    return this.uploadedImageUrl.split('/').pop() || 'unknown.jpg';
  }

  getUploadedImageFormat(): string {
    if (!this.uploadedImageUrl) return '';
    const extension = this.uploadedImageUrl.split('.').pop()?.toUpperCase();
    return extension || 'Unknown';
  }

  deleteUploadedImage(): void {
    if (!this.uploadedImageUrl) return;
    
    if (!confirm('আপনি কি নিশ্চিত যে এই আপলোড করা ইমেজটি ডিলিট করতে চান?')) {
      return;
    }

    this.deleteImage(this.uploadedImageUrl);
  }

  shareImage(type: string): void {
    if (!this.uploadedImageUrl) return;

    let content = '';
    let title = '';

    switch (type) {
      case 'link':
        content = this.uploadedImageUrl;
        title = 'ইমেজ লিংক কপি করা হয়েছে';
        break;
      case 'embed':
        content = `<img src="${this.uploadedImageUrl}" alt="${this.getUploadedImageName()}" style="max-width: 100%; height: auto;">`;
        title = 'এম্বেড কোড কপি করা হয়েছে';
        break;
      case 'html':
        content = `<img src="${this.uploadedImageUrl}" alt="${this.getUploadedImageName()}" width="auto" height="auto">`;
        title = 'HTML কোড কপি করা হয়েছে';
        break;
      default:
        content = this.uploadedImageUrl;
        title = 'লিংক কপি করা হয়েছে';
    }

    navigator.clipboard.writeText(content).then(() => {
      this.showToast(title, 'success');
    }, (err) => {
      this.showToast('কপি করতে ব্যর্থ', 'error');
      console.error('Could not copy text: ', err);
    });
  }

  // Gallery enhancement methods
  getImageFormat(imageUrl: string): string {
    if (!imageUrl) return '';
    const extension = imageUrl.split('.').pop()?.toUpperCase();
    return extension || 'Unknown';
  }

  getImageFileSize(imageUrl: string): string {
    // This would ideally come from server metadata
    // For now, return placeholder
    return '';
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.imageFilter = 'all';
    this.filterImages();
  }

  clearFilters(): void {
    this.searchTerm = '';
    this.imageFilter = 'all';
    this.sortOption = 'name-asc';
    this.selectedImages = []; // Clear all selections when clearing filters
    this.filterImages();
    this.sortImages();
  }

  applyFilter(): void {
    this.filterImages();
  }

  sortImages(): void {
    switch (this.sortOption) {
      case 'name-asc':
        this.filteredImages.sort((a, b) => 
          this.getImageName(a).localeCompare(this.getImageName(b))
        );
        break;
      case 'name-desc':
        this.filteredImages.sort((a, b) => 
          this.getImageName(b).localeCompare(this.getImageName(a))
        );
        break;
      case 'date-newest':
        this.filteredImages.reverse();
        break;
      case 'date-oldest':
        this.filteredImages.sort();
        break;
    }
    this.cleanupSelections(); // Clean up selections after sorting
    this.updatePagination();
  }

  setGalleryView(view: string): void {
    this.galleryView = view;
    if (view === 'list') {
      this.imagesPerPage = 20;
    } else {
      this.imagesPerPage = 12;
    }
    this.updatePagination();
  }

  // Selection methods
  isImageSelected(image: string): boolean {
    return this.selectedImages.includes(image);
  }

  toggleImageSelection(image: string, event: any): void {
    if (event.target.checked) {
      if (!this.selectedImages.includes(image)) {
        this.selectedImages.push(image);
      }
    } else {
      this.selectedImages = this.selectedImages.filter(img => img !== image);
    }
  }

  // Check if all visible (paginated) images are selected
  isAllVisibleSelected(): boolean {
    return this.paginatedImages.length > 0 && 
           this.paginatedImages.every(image => this.selectedImages.includes(image));
  }

  // Check if some visible (paginated) images are selected
  isSomeVisibleSelected(): boolean {
    const selectedVisibleCount = this.paginatedImages.filter(image => 
      this.selectedImages.includes(image)
    ).length;
    return selectedVisibleCount > 0 && selectedVisibleCount < this.paginatedImages.length;
  }

  // Legacy methods for backward compatibility (but use visible-based logic)
  isAllSelected(): boolean {
    return this.isAllVisibleSelected();
  }

  isSomeSelected(): boolean {
    return this.isSomeVisibleSelected();
  }

  toggleSelectAll(event: any): void {
    if (event.target.checked) {
      // Add all visible images to selection (avoiding duplicates)
      this.paginatedImages.forEach(image => {
        if (!this.selectedImages.includes(image)) {
          this.selectedImages.push(image);
        }
      });
    } else {
      // Remove all visible images from selection
      this.selectedImages = this.selectedImages.filter(image => 
        !this.paginatedImages.includes(image)
      );
    }
  }

  getSelectAllText(): string {
    if (this.isAllVisibleSelected()) {
      return `এই পাতার সব বাতিল করুন (${this.paginatedImages.length})`;
    } else if (this.isSomeVisibleSelected()) {
      return `এই পাতার সব নির্বাচন করুন (${this.paginatedImages.length})`;
    }
    return `এই পাতার সব নির্বাচন করুন (${this.paginatedImages.length})`;
  }

  // Get selection context information
  getSelectionContext(): string {
    if (this.selectedImages.length === 0) return '';
    
    const selectedOnCurrentPage = this.paginatedImages.filter(image => 
      this.selectedImages.includes(image)
    ).length;
    
    if (this.selectedImages.length === selectedOnCurrentPage) {
      return `${this.selectedImages.length} টি নির্বাচিত (এই পাতায়)`;
    } else {
      return `${this.selectedImages.length} টি নির্বাচিত (${selectedOnCurrentPage} এই পাতায়, ${this.selectedImages.length - selectedOnCurrentPage} অন্য পাতায়)`;
    }
  }

  clearSelection(): void {
    this.selectedImages = [];
  }

  // Clean up selections that are no longer in filtered results
  private cleanupSelections(): void {
    this.selectedImages = this.selectedImages.filter(image => 
      this.filteredImages.includes(image)
    );
  }

  // Bulk actions
  bulkCopyUrls(): void {
    const urls = this.selectedImages.join('\n');
    navigator.clipboard.writeText(urls).then(() => {
      this.showToast(`${this.selectedImages.length} টি URL কপি করা হয়েছে`, 'success');
    }, (err) => {
      this.showToast('URL কপি করতে ব্যর্থ', 'error');
    });
  }

  bulkDownload(): void {
    this.selectedImages.forEach((imageUrl, index) => {
      setTimeout(() => {
        const link = document.createElement('a');
        link.href = imageUrl;
        link.download = this.getImageName(imageUrl);
        link.click();
      }, index * 100); // Stagger downloads
    });
    this.showToast(`${this.selectedImages.length} টি ইমেজ ডাউনলোড শুরু হয়েছে`, 'success');
  }

  bulkDelete(): void {
    if (!confirm(`আপনি কি নিশ্চিত যে ${this.selectedImages.length} টি ইমেজ ডিলিট করতে চান?`)) {
      return;
    }

    const deletePromises = this.selectedImages.map(imageUrl => {
      const filename = imageUrl.split('/').pop();
      return this.http.delete(`${environment.apiBaseUrl}/api/admin/delete-image/${filename}`).toPromise();
    });

    Promise.all(deletePromises).then(() => {
      this.showToast(`${this.selectedImages.length} টি ইমেজ সফলভাবে ডিলিট হয়েছে`, 'success');
      this.selectedImages = [];
      this.loadAllImages();
    }).catch(error => {
      this.showToast('কিছু ইমেজ ডিলিট করতে ব্যর্থ', 'error');
      console.error('Bulk delete error:', error);
    });
  }

  // Pagination methods
  updatePagination(): void {
    this.totalPages = Math.ceil(this.filteredImages.length / this.imagesPerPage);
    if (this.currentPage > this.totalPages) {
      this.currentPage = 1;
    }
    this.updatePaginatedImages();
  }

  updatePaginatedImages(): void {
    const startIndex = (this.currentPage - 1) * this.imagesPerPage;
    const endIndex = startIndex + this.imagesPerPage;
    this.paginatedImages = this.filteredImages.slice(startIndex, endIndex);
  }

  goToPage(page: number | string): void {
    if (typeof page === 'string') return;
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.updatePaginatedImages();
    }
  }

  getPageNumbers(): (number | string)[] {
    const pages: (number | string)[] = [];
    const totalPages = this.totalPages;
    const currentPage = this.currentPage;

    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      
      if (currentPage <= 4) {
        for (let i = 2; i <= 5; i++) {
          pages.push(i);
        }
        pages.push('...');
      } else if (currentPage >= totalPages - 3) {
        pages.push('...');
        for (let i = totalPages - 4; i <= totalPages - 1; i++) {
          pages.push(i);
        }
      } else {
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
      }
      
      pages.push(totalPages);
    }

    return pages;
  }

  getPaginationInfo(): string {
    const start = (this.currentPage - 1) * this.imagesPerPage + 1;
    const end = Math.min(this.currentPage * this.imagesPerPage, this.filteredImages.length);
    const total = this.filteredImages.length;
    return `${start}-${end} এর ${total} টি`;
  }

  // Image loading state methods
  isImageLoaded(image: string): boolean {
    return this.loadedImages.has(image);
  }

  onImageError(event: any): void {
    console.error('Image failed to load:', event.target.src);
  }

  scrollToUpload(): void {
    const uploadSection = document.querySelector('.content-card');
    if (uploadSection) {
      uploadSection.scrollIntoView({ behavior: 'smooth' });
    }
  }
}
